import type {
  UserAttributes,
  VolunteerRoleApplicationSubmissionAttributes,
  VolunteerRoleAttributes,
  WorldAttributes,
} from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOffsetToSQL,
  queryOrderToSQL,
} from './common'
import UserQ from './UserQ'
import VolunteerRoleQ from './VolunteerRoleQ'
import WorldQ from './WorldQ'

// Keep displaying submissions as "current" for this many months. If we want to
// run applications on shorter timelines, we'll need to improve this.
const MONTHS_TO_SHOW_APPLICATION = 2

const VolunteerRoleApplicationSubmissionQ = {
  ...makeQueryModelV2<VolunteerRoleApplicationSubmissionAttributes>(
    'volunteer_role_application_submission'
  )([
    'id',
    'volunteer_role_id',
    'user_id',
    'world_id',
    'general_responses',
    'role_responses',
    'reviewer_user_id',
    'review_message',
    'withdrawn_at',
    'rejected_at',
    'offer_extended_at',
    'offer_accepted_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    VolunteerRole: {
      query: VolunteerRoleQ,
      foreignKey: VolunteerRoleQ.id,
      sourceKey: VolunteerRoleApplicationSubmissionQ.volunteer_role_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: VolunteerRoleApplicationSubmissionQ.user_id,
    },
  }),

  where: (
    {
      id,
      volunteerRoleId,
      userId,
      hasBeenWithdrawn,
      hasBeenRejected,
      hasGeneralResponses,
      include = [],
      paranoid = true,
      groupBy = null,
      order = null,
      limit = null,
      offset = null,
    }: {
      id?: number | null | SQLFragment<number>
      volunteerRoleId?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      hasBeenWithdrawn?: boolean | null
      hasBeenRejected?: boolean | null
      hasGeneralResponses?: boolean | null
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
      limit?: number | null
      offset?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<VolunteerRoleApplicationSubmissionAttributes> => SQL`
        FROM ${VolunteerRoleApplicationSubmissionQ}
        ${includeAssociationsToSql(VolunteerRoleApplicationSubmissionQ, include)}
        WHERE
          ${id != null ? SQL`${VolunteerRoleApplicationSubmissionQ.id} = ${id} AND` : SQL``}
          ${
            volunteerRoleId != null
              ? SQL`${VolunteerRoleApplicationSubmissionQ.volunteer_role_id} = ${volunteerRoleId} AND`
              : SQL``
          }
          ${
            userId != null
              ? SQL`${VolunteerRoleApplicationSubmissionQ.user_id} = ${userId} AND`
              : SQL``
          }
          ${
            hasBeenWithdrawn == null
              ? SQL``
              : hasBeenWithdrawn
                ? SQL`${VolunteerRoleApplicationSubmissionQ.withdrawn_at} IS NOT NULL AND`
                : SQL`${VolunteerRoleApplicationSubmissionQ.withdrawn_at} IS NULL AND`
          }
          ${
            hasBeenRejected == null
              ? SQL``
              : hasBeenRejected
                ? SQL`${VolunteerRoleApplicationSubmissionQ.rejected_at} IS NOT NULL AND`
                : SQL`${VolunteerRoleApplicationSubmissionQ.rejected_at} IS NULL AND`
          }
          ${
            hasGeneralResponses == null
              ? SQL``
              : hasGeneralResponses
                ? SQL`${VolunteerRoleApplicationSubmissionQ.general_responses} IS NOT NULL AND`
                : SQL`${VolunteerRoleApplicationSubmissionQ.general_responses} IS NULL AND`
          }
          ${paranoid ? SQL`${VolunteerRoleApplicationSubmissionQ.deleted_at} IS NULL AND` : SQL``}
          ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
        ${queryOffsetToSQL(offset)}
    `,

  getRole: (): SQLFragment<VolunteerRoleAttributes> =>
    VolunteerRoleQ.where({
      id: VolunteerRoleApplicationSubmissionQ.volunteer_role_id,
    }),

  getUser: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: VolunteerRoleApplicationSubmissionQ.user_id,
    }),

  getWorld: (): SQLFragment<WorldAttributes> =>
    WorldQ.where({ id: VolunteerRoleApplicationSubmissionQ.world_id }),

  needsReview: (): SQLFragment<boolean> =>
    SQL`(
      ${VolunteerRoleApplicationSubmissionQ.withdrawn_at} IS NULL
      AND ${VolunteerRoleApplicationSubmissionQ.rejected_at} IS NULL
      AND ${VolunteerRoleApplicationSubmissionQ.offer_extended_at} IS NULL
    )`,

  awaitingResponse: (): SQLFragment<boolean> =>
    SQL`(
      ${VolunteerRoleApplicationSubmissionQ.offer_extended_at} IS NOT NULL
      AND ${VolunteerRoleApplicationSubmissionQ.offer_accepted_at} IS NULL
      AND ${VolunteerRoleApplicationSubmissionQ.withdrawn_at} IS NULL
    )`,

  getMostRecentUserSubmission: ({
    userId,
    hasGeneralResponses,
  }: {
    userId: number | SQLFragment<number>
    hasGeneralResponses: boolean | null
  }): SQLFragment<VolunteerRoleApplicationSubmissionAttributes> =>
    VolunteerRoleApplicationSubmissionQ.where({
      userId,
      hasGeneralResponses,
      order: [[VolunteerRoleApplicationSubmissionQ.created_at, 'desc']],
      limit: 1,
    }),

  getCurrentSubmission: ({
    userId,
    volunteerRoleId,
  }: {
    userId: number | SQLFragment<number>
    volunteerRoleId: number | SQLFragment<number>
  }): SQLFragment<VolunteerRoleApplicationSubmissionAttributes> =>
    SQL`${VolunteerRoleApplicationSubmissionQ.where(
      {
        userId,
        volunteerRoleId,
        order: [[VolunteerRoleApplicationSubmissionQ.created_at, 'desc']],
        limit: 1,
      },
      SQL`${VolunteerRoleApplicationSubmissionQ.created_at} >= NOW() - ${MONTHS_TO_SHOW_APPLICATION} * INTERVAL '1 month'`
    )}`,

  isOfferAccepted: (): SQLFragment<boolean> =>
    SQL`${VolunteerRoleApplicationSubmissionQ.offer_accepted_at} IS NOT NULL`,
}

export default VolunteerRoleApplicationSubmissionQ
